import { useState, useRef, useCallback, useEffect } from 'react';

export const useMediaPlayer = () => {
  const [currentVideoTime, setCurrentVideoTime] = useState(0);
  const [sessionDuration, setSessionDuration] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [videoFiles, setVideoFiles] = useState([]);
  const [visibleFiles, setVisibleFiles] = useState([]);
  
  const videoRefs = useRef([]);
  const audioRef = useRef(null);
  const playbackIntervalRef = useRef(null);
  const lastUpdateTimeRef = useRef(Date.now());

  // Independent playback timer
  useEffect(() => {
    if (isPlaying) {
      lastUpdateTimeRef.current = Date.now();
      playbackIntervalRef.current = setInterval(() => {
        const now = Date.now();
        const elapsed = (now - lastUpdateTimeRef.current) / 1000;
        lastUpdateTimeRef.current = now;
        
        setCurrentVideoTime(prev => {
          const newTime = prev + elapsed;
          return newTime >= sessionDuration ? sessionDuration : newTime;
        });
      }, 100);
    } else {
      if (playbackIntervalRef.current) {
        clearInterval(playbackIntervalRef.current);
        playbackIntervalRef.current = null;
      }
    }
    
    return () => {
      if (playbackIntervalRef.current) {
        clearInterval(playbackIntervalRef.current);
      }
    };
  }, [isPlaying, sessionDuration]);

  const resetMediaElements = useCallback(() => {
    if (playbackIntervalRef.current) {
      clearInterval(playbackIntervalRef.current);
      playbackIntervalRef.current = null;
    }
    videoRefs.current.forEach(videoRef => {
      if (videoRef) {
        videoRef.pause();
        videoRef.currentTime = 0;
        videoRef.src = '';
      }
    });
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      audioRef.current.src = '';
    }
    setCurrentVideoTime(0);
    setSessionDuration(0);
    setIsPlaying(false);
    setVideoFiles([]);
    setVisibleFiles([]);
  }, []);

  const handlePlayheadChange = useCallback((newTime) => {
    setCurrentVideoTime(newTime);
    videoRefs.current.forEach(videoRef => {
      if (videoRef) {
        videoRef.currentTime = newTime;
      }
    });
    if (audioRef.current) {
      audioRef.current.currentTime = newTime;
    }
  }, []);

  const handlePlayPause = useCallback(() => {
    setIsPlaying(prev => {
      const newIsPlaying = !prev;
      if (newIsPlaying) {
        videoRefs.current.forEach(videoRef => {
          if (videoRef) videoRef.play();
        });
        if (audioRef.current) {
          audioRef.current.play().catch(e => console.log('Audio play interrupted:', e.name));
        }
      } else {
        videoRefs.current.forEach(videoRef => {
          if (videoRef) videoRef.pause();
        });
        if (audioRef.current) {
          audioRef.current.pause();
        }
      }
      return newIsPlaying;
    });
  }, []);

  return {
    currentVideoTime,
    setCurrentVideoTime,
    sessionDuration,
    setSessionDuration,
    isPlaying,
    setIsPlaying,
    videoFiles,
    setVideoFiles,
    visibleFiles,
    setVisibleFiles,
    videoRefs,
    audioRef,
    resetMediaElements,
    handlePlayheadChange,
    handlePlayPause
  };
};